import { useMessageWaitingRows } from './messageWaiting';
import type { UserDoc } from './users';

export {
  recentWaitingMentions,
  stillUnreadMentions,
  waitingOnYouCount,
  waitingCheckFailed,
  waitingCountLabel,
  waitingCountState,
} from './staffWaitingOnYouCore';

/**
 * The staff "Waiting on you" number: the bell, the phone app badge, the
 * Messages tile and the menu all read this, and the staff home row reads the
 * same hook (B17). It is every row Messages shows as unread, by the rule in
 * messagesUnreadCore.ts that Messages itself marks its rows with, so the
 * number and the list should agree. Before B17 this added must-reads and
 * mentions one message at a time and left out every bell card row, new
 * replies on a staff announcement, and owed studio announcements and class
 * posts; Messages showed those and the number did not, and a must-read in a
 * conversation she had read counted here with nothing unread in Messages.
 *
 * Staff announcements are part of it now. The tile and the menu used to add
 * the announcement count on top of this one; they no longer do.
 *
 * Takes the viewer's own users doc as the caller already holds it, never
 * fetching its own copy (screen-read-budget.test.mjs): her mutedIds, so a
 * fully muted admin board goes quiet here exactly as on its Messages row, and
 * the rest of what Messages decides with it (her other sign-ins, the staff
 * announcement door, the FOH class post tuck).
 */
export function useStaffMessageWaitingCount(userDoc: UserDoc | null | undefined): {
  count: number;
  loading: boolean;
  error: string | null;
} {
  // No `capped` here on purpose: a full list is not a failure, and the bell,
  // the badge and the tile show the number found (WaitingCountState).
  const waiting = useMessageWaitingRows({ mutedIds: userDoc?.notifPrefs?.mutedIds, staff: true, userDoc });
  return { count: waiting.count, loading: waiting.loading, error: waiting.error };
}

/** The exact count used by the staff home row, for the bell and the badge. */
export function useStaffWaitingOnYouCount(userDoc: UserDoc | null | undefined): {
  count: number;
  loading: boolean;
  error: string | null;
} {
  return useStaffMessageWaitingCount(userDoc);
}
